import React, { useState, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ScrollText, ChevronRight, ChevronDown, Undo2 } from "lucide-react";

const ACTION = { create: "Δημιουργία", update: "Αλλαγή", delete: "Διαγραφή", erase: "Οριστική διαγραφή", restore: "Επαναφορά", approve: "Έγκριση", reject: "Απόρριψη", send: "Αποστολή", cancel: "Ακύρωση" };
const ACTION_COLOR = {
  create: "bg-[#E7EAE0] text-[#5C6652]", update: "bg-[#F3EADF] text-[#9A6A3A]",
  delete: "bg-[#F6E2DD] text-[#A1493A]", erase: "bg-[#A1493A] text-white", restore: "bg-[#E2ECE4] text-[#3F6B4C]", approve: "bg-[#E2ECE4] text-[#3F6B4C]",
  reject: "bg-[#F6E2DD] text-[#A1493A]", send: "bg-[#E6EAF0] text-[#45607F]", cancel: "bg-[#EFEBE4] text-[#6F6A60]",
};
const TYPE = { payroll: "Μισθοδοσία", invoices: "Τιμολόγια", expenses: "Έξοδα", orders: "Παραγγελίες", app_users: "Χρήστες", employees: "Υπάλληλοι", tt_members: "Μέλη Team", payroll_runs: "Μισθοδοσίες", erp_docs: "Παραστατικά SoftOne", erp_items: "Είδη SoftOne" };
const REASON = { erase: "Οριστική διαγραφή", gdpr_delete: "GDPR διαγραφή", bulk_erase: "Μαζική διαγραφή", employees: "Οριστική διαγραφή υπαλλήλου", erp_delete: "Διαγραφή SoftOne" };

function Erasures() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["erasures"], queryFn: async () => (await api.get("/erasures")).data });
  const restore = useMutation({
    mutationFn: async (r) => (await api.post("/erasures/restore", { coll: r.coll, at: r.at })).data,
    onSuccess: (d) => { toast.success(`Επαναφέρθηκαν ${d.restored} εγγραφές`); ["erasures", "audit-log", "employees", "team-members", "runs", "erp-imports"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="erasures-card">
      <div className="px-4 py-3 border-b border-[#E3DED4]">
        <h2 className="font-medium text-[#45423C] inline-flex items-center gap-2"><Undo2 className="h-4 w-4 text-[#A1493A]" /> Οριστικές διαγραφές — επαναφορά από backup</h2>
        <p className="text-xs text-[#6F6A60] mt-0.5">Ό,τι σβήστηκε οριστικά κρατείται σε αντίγραφο ασφαλείας. Με ένα κλικ επανέρχεται στη θέση του.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]"><tr><th className="px-3 py-2 text-left">Πότε</th><th className="px-3 text-left">Ποιος</th><th className="px-3 text-left">Τι</th><th className="px-3 text-left">Εγγραφή</th><th className="px-3 text-right">Πλήθος</th><th className="px-3" /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.coll + r.at} className="border-t border-[#E3DED4]" data-testid={`erasure-${r.coll}-${r.at}`}>
                <td className="px-3 py-2 whitespace-nowrap">{new Date(r.at).toLocaleString("el-GR")}</td>
                <td className="px-3">{r.by || "—"}</td>
                <td className="px-3">{TYPE[r.coll] || r.coll} · <span className="text-[#6F6A60]">{REASON[r.reason] || r.reason}</span></td>
                <td className="px-3">{r.title || (r.year ? `${r.kind || ""} ${r.year}` : "—")}</td>
                <td className="px-3 text-right tabular">{r.count}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid="erasure-restore" disabled={restore.isPending} onClick={() => window.confirm(`Επαναφορά ${r.count} εγγραφών (${TYPE[r.coll] || r.coll});`) && restore.mutate(r)}
                    className="rounded-lg border border-[#3F6B4C] text-[#3F6B4C] px-3 py-1 text-xs font-semibold hover:bg-[#E2ECE4] disabled:opacity-50">Επαναφορά</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Diff({ before, after }) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (!keys.length) return <span className="text-[#8A8577] text-xs">—</span>;
  const fmt = (v) => (v === null || v === undefined || v === "" ? "∅" : typeof v === "object" ? JSON.stringify(v) : String(v));
  return (
    <div className="space-y-1" data-testid="audit-diff">
      {keys.map((k) => {
        const b = before ? before[k] : undefined;
        const a = after ? after[k] : undefined;
        const changed = JSON.stringify(b) !== JSON.stringify(a);
        return (
          <div key={k} className="flex items-start gap-2 text-xs">
            <span className="font-mono text-[#8A8577] min-w-[110px]">{k}</span>
            {before !== null && before !== undefined && (
              <span className={`px-1.5 py-0.5 rounded ${changed ? "bg-[#F6E2DD] text-[#A1493A] line-through" : "text-[#6F6A60]"}`}>{fmt(b)}</span>
            )}
            {changed && after !== null && after !== undefined && <span className="text-[#8A8577]">→</span>}
            {after !== null && after !== undefined && changed && (
              <span className="px-1.5 py-0.5 rounded bg-[#E2ECE4] text-[#3F6B4C]">{fmt(a)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AuditLog() {
  const [recordType, setRecordType] = useState("");
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["audit-log", recordType, action, actor, dateFrom, dateTo],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (recordType) p.set("record_type", recordType);
      if (action) p.set("action", action);
      if (actor) p.set("actor", actor);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      return (await api.get(`/audit-log?${p.toString()}`)).data;
    },
  });
  const rows = data?.rows || [];
  const recordTypes = data?.record_types || [];
  const actors = data?.actors || [];
  const selCls = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";

  const reset = () => { setRecordType(""); setAction(""); setActor(""); setDateFrom(""); setDateTo(""); };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">Owner</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2">
          <ScrollText className="h-7 w-7" /> Ιστορικό Αλλαγών
        </h1>
        <p className="text-[#6F6A60] text-sm mt-1">Καταγραφή ενεργειών σε χρήστες, υπαλλήλους, μισθοδοσία, παραστατικά, έξοδα & παραγγελίες. Πάτα μια γραμμή για το πριν → μετά.</p>
      </div>

      <Erasures />

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 flex flex-wrap items-end gap-3" data-testid="audit-filters">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Τύπος</label>
          <select data-testid="audit-filter-type" className={selCls} value={recordType} onChange={(e) => setRecordType(e.target.value)}>
            <option value="">Όλοι</option>
            {recordTypes.map((t) => <option key={t} value={t}>{TYPE[t] || t}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Ενέργεια</label>
          <select data-testid="audit-filter-action" className={selCls} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">Όλες</option>
            {Object.keys(ACTION).map((a) => <option key={a} value={a}>{ACTION[a]}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Χρήστης</label>
          <select data-testid="audit-filter-user" className={selCls} value={actor} onChange={(e) => setActor(e.target.value)}>
            <option value="">Όλοι</option>
            {actors.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Από</label>
          <input data-testid="audit-filter-from" type="date" className={selCls} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Έως</label>
          <input data-testid="audit-filter-to" type="date" className={selCls} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <button data-testid="audit-filter-reset" onClick={reset} className="px-4 py-2 rounded-xl border border-[#E3DED4] text-sm text-[#6F6A60] hover:bg-[#F7F5F1]">Καθαρισμός</button>
        <span className="ml-auto text-xs text-[#8A8577]" data-testid="audit-count">{rows.length} εγγραφές</span>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3 w-6"></th>
                <th className="px-4 py-3">Ώρα</th><th className="px-4 py-3">Χρήστης</th><th className="px-4 py-3">Ρόλος</th>
                <th className="px-4 py-3">Ενέργεια</th><th className="px-4 py-3">Τύπος</th><th className="px-4 py-3">Εγγραφή</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {!isLoading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6F6A60]">Καμία καταγραφή.</td></tr>}
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr data-testid={`audit-${r.id}`} className="border-t border-[#E3DED4] hover:bg-[#FAF8F4] cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}>
                    <td className="px-4 py-2 text-[#8A8577]">{open[r.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                    <td className="px-4 py-2 text-[#6F6A60]">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-2 font-medium text-[#45423C]">{r.user}</td>
                    <td className="px-4 py-2 text-[#6F6A60]">{r.role}</td>
                    <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${ACTION_COLOR[r.action] || "bg-[#EFEBE4] text-[#6F6A60]"}`}>{ACTION[r.action] || r.action}</span></td>
                    <td className="px-4 py-2 text-[#6F6A60]">{TYPE[r.record_type] || r.record_type}</td>
                    <td className="px-4 py-2 text-[#6F6A60] text-xs">{r.record_id}</td>
                  </tr>
                  {open[r.id] && (
                    <tr className="border-t border-[#F1EDE6] bg-[#FBFAF7]" data-testid={`audit-detail-${r.id}`}>
                      <td></td>
                      <td colSpan={6} className="px-4 py-3"><Diff before={r.before} after={r.after} /></td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
